import { Charge, LoaBillConfig, LoaSchedule } from '../../../core/models/domain.models';

export interface ContractChargePartyLike {
  loaId: string;
  quantum?: number | string | null;
  rldc?: string | null;
}

export interface ContractChargeApplicationLike {
  id: string;
  contract_id?: string | null;
  application_number?: string | null;
  seller_loa_id?: string | null;
  buyer_loa_id?: string | null;
  approval_charges?: Array<{
    group_name: string;
    section_heading?: string | null;
    sub_group_name: string;
    rate_mwh?: number | null;
    quantum_mwh?: number | null;
    amount?: number | null;
  }>;
}

export interface ContractChargeBreakdownRow {
  applicationNumber: string;
  chargeType: string;
  rateType?: string | null;
  totalAmount: number;
  buyerAmount: number;
  sellerAmount: number;
  seciAmount: number;
  allocationLabel: string;
}

export interface ContractChargeBreakdownSummary {
  totalAmount: number;
  buyerAmount: number;
  sellerAmount: number;
  seciAmount: number;
  rows: ContractChargeBreakdownRow[];
}

interface OaChargeAllocation {
  charge_type: string;
  payer: 'Buyer' | 'Seller' | 'Shared';
  buyer_percent: number;
  seller_percent: number;
}

interface ContractChargeBasis {
  days: number;
  energyMwh: number;
}

interface ContractChargeBreakdownOptions {
  contractId: string;
  cutoffDate: string;
  periodFrom: string;
  periodTo: string;
  partyDetails: ContractChargePartyLike[];
  applications: ContractChargeApplicationLike[];
  chargeMasters: Charge[];
  billConfigsByLoa: Record<string, LoaBillConfig[]>;
  loaSchedulesByLoa: Record<string, LoaSchedule[]>;
}

export function buildContractChargeBreakdown(options: ContractChargeBreakdownOptions): ContractChargeBreakdownSummary {
  const configured = buildConfiguredChargeBreakdown(options);
  if (configured.rows.length > 0) return configured;
  return buildPunchedChargeBreakdown(options);
}

function buildConfiguredChargeBreakdown(options: ContractChargeBreakdownOptions): ContractChargeBreakdownSummary {
  const charges = configuredChargesForContract(options.chargeMasters, options.cutoffDate);
  if (!charges.length) return emptySummary();

  const loaIds = options.partyDetails.map((row) => String(row.loaId || '').trim()).filter((value) => !!value);
  if (!loaIds.length) return emptySummary();

  const allocationMap = oaChargeAllocationMapFromLoaIds(loaIds, options.billConfigsByLoa);
  const contractRegionCodes = contractRegionCodesFromPartyDetails(options.partyDetails);
  const basis = resolveContractChargeBasis(options.partyDetails, options.loaSchedulesByLoa, options.periodFrom, options.periodTo);

  let totalAmount = 0;
  let buyerAmount = 0;
  let sellerAmount = 0;
  let seciAmount = 0;
  const rows: ContractChargeBreakdownRow[] = [];

  for (const charge of charges) {
    const baseValue = Number(charge.value);
    if (!Number.isFinite(baseValue) || baseValue <= 0) continue;
    const requiredRegion = extractRegionCode(charge.charge_name);
    if (requiredRegion && !contractRegionCodes.has(requiredRegion)) continue;

    const totalChargeAmount = resolveConfiguredChargeAmount(charge, basis);
    if (!Number.isFinite(totalChargeAmount) || totalChargeAmount <= 0) continue;

    const allocationKey = normalizeChargeAllocationType(`${charge.charge_name || ''} ${charge.charge_type || ''}`.trim());
    const allocation = allocationMap.get(allocationKey);
    const buyerPercent = sanitizePercent(allocation?.buyer_percent);
    const sellerPercent = cappedSellerPercent(allocation?.seller_percent, buyerPercent);
    const mappedPercent = buyerPercent + sellerPercent;
    const buyerShare = (totalChargeAmount * buyerPercent) / 100;
    const sellerShare = (totalChargeAmount * sellerPercent) / 100;
    const seciShare = Math.max(totalChargeAmount - buyerShare - sellerShare, 0);

    totalAmount += totalChargeAmount;
    buyerAmount += buyerShare;
    sellerAmount += sellerShare;
    seciAmount += seciShare;
    rows.push({
      applicationNumber: String(charge.charge_type || '').trim() || 'Charge Master',
      chargeType: String(charge.charge_name || '').trim() || allocationKey || 'Charge',
      rateType: (charge.rate_type || '').trim() || null,
      totalAmount: Number(totalChargeAmount.toFixed(2)),
      buyerAmount: Number(buyerShare.toFixed(2)),
      sellerAmount: Number(sellerShare.toFixed(2)),
      seciAmount: Number(seciShare.toFixed(2)),
      allocationLabel: `Effective ${charge.effective_date} • Buyer ${formatNumber(buyerPercent)}% / Seller ${formatNumber(sellerPercent)}% / SECI ${formatNumber(Math.max(100 - mappedPercent, 0))}%`,
    });
  }

  return {
    totalAmount: Number(totalAmount.toFixed(2)),
    buyerAmount: Number(buyerAmount.toFixed(2)),
    sellerAmount: Number(sellerAmount.toFixed(2)),
    seciAmount: Number(seciAmount.toFixed(2)),
    rows,
  };
}

function buildPunchedChargeBreakdown(options: ContractChargeBreakdownOptions): ContractChargeBreakdownSummary {
  const rows: ContractChargeBreakdownRow[] = [];
  const basis = resolveContractChargeBasis(options.partyDetails, options.loaSchedulesByLoa, options.periodFrom, options.periodTo);
  const contractRegionCodes = contractRegionCodesFromPartyDetails(options.partyDetails);

  for (const application of options.applications) {
    if (String(application.contract_id || '').trim() !== String(options.contractId || '').trim()) continue;
    const approvalCharges = application.approval_charges || [];
    if (!approvalCharges.length) continue;

    const allocationMap = oaChargeAllocationMap(application, options.billConfigsByLoa);

    for (const [index, charge] of approvalCharges.entries()) {
      const allocationKey = normalizeApplicationChargeType(charge, index);
      const master = resolveChargeMasterFromMasters(options.chargeMasters, options.cutoffDate, allocationKey, contractRegionCodes);
      const rateType = String(master?.rate_type || '').trim() || null;
      const masterValue = master ? Number(master.value) : Number.NaN;
      const fallbackAmount = resolveApplicationChargeAmount(charge);
      const displayChargeName = String(master?.charge_name || '').trim() || allocationKey;
      const regionLabel = master?.charge_name || allocationKey;
      const requiredRegion = extractRegionCode(regionLabel);
      if (requiredRegion && !contractRegionCodes.has(requiredRegion)) continue;
      const totalAmount = master && Number.isFinite(masterValue) ? resolveChargeAmountFromRate(masterValue, rateType, basis) : fallbackAmount;
      const allocation = allocationMap.get(allocationKey);
      const buyerPercent = sanitizePercent(allocation?.buyer_percent);
      const sellerPercent = cappedSellerPercent(allocation?.seller_percent, buyerPercent);
      const mappedPercent = buyerPercent + sellerPercent;
      const buyerAmount = (totalAmount * buyerPercent) / 100;
      const sellerAmount = (totalAmount * sellerPercent) / 100;
      const seciAmount = Math.max(totalAmount - buyerAmount - sellerAmount, 0);

      rows.push({
        applicationNumber: application.application_number || String(application.id),
        chargeType: displayChargeName,
        rateType,
        totalAmount: Number(totalAmount.toFixed(2)),
        buyerAmount: Number(buyerAmount.toFixed(2)),
        sellerAmount: Number(sellerAmount.toFixed(2)),
        seciAmount: Number(seciAmount.toFixed(2)),
        allocationLabel: !allocation
          ? 'No buyer/seller mapping found, so the remaining amount is counted on SECI'
          : `Buyer ${formatNumber(buyerPercent)}% / Seller ${formatNumber(sellerPercent)}% / SECI ${formatNumber(Math.max(100 - mappedPercent, 0))}%`,
      });
    }
  }

  return aggregateChargeRows(rows);
}

function aggregateChargeRows(rows: ContractChargeBreakdownRow[]): ContractChargeBreakdownSummary {
  const grouped = new Map<string, ContractChargeBreakdownRow>();
  for (const row of rows) {
    const key = [row.chargeType, row.rateType || '', row.allocationLabel].join('|');
    const existing = grouped.get(key);
    if (!existing) {
      grouped.set(key, { ...row });
      continue;
    }
    existing.totalAmount = Number((existing.totalAmount + row.totalAmount).toFixed(2));
    existing.buyerAmount = Number((existing.buyerAmount + row.buyerAmount).toFixed(2));
    existing.sellerAmount = Number((existing.sellerAmount + row.sellerAmount).toFixed(2));
    existing.seciAmount = Number((existing.seciAmount + row.seciAmount).toFixed(2));
  }

  const summaryRows = [...grouped.values()];
  return {
    totalAmount: Number(summaryRows.reduce((sum, row) => sum + row.totalAmount, 0).toFixed(2)),
    buyerAmount: Number(summaryRows.reduce((sum, row) => sum + row.buyerAmount, 0).toFixed(2)),
    sellerAmount: Number(summaryRows.reduce((sum, row) => sum + row.sellerAmount, 0).toFixed(2)),
    seciAmount: Number(summaryRows.reduce((sum, row) => sum + row.seciAmount, 0).toFixed(2)),
    rows: summaryRows,
  };
}

function resolveContractChargeBasis(
  partyDetails: ContractChargePartyLike[],
  loaSchedulesByLoa: Record<string, LoaSchedule[]>,
  periodFrom: string,
  periodTo: string,
): ContractChargeBasis {
  if (!partyDetails.length) return { days: 0, energyMwh: 0 };

  const contractStart = parseDateOnlyFromString(periodFrom);
  const contractEnd = parseDateOnlyFromString(periodTo);
  if (!contractStart || !contractEnd) return { days: 0, energyMwh: 0 };

  const daysDifference = inclusiveDayCount(contractStart, contractEnd);
  const energyByLoa = new Map<string, number>();

  for (const party of partyDetails) {
    const loaId = String(party.loaId || '').trim();
    if (!loaId || energyByLoa.has(loaId)) continue;

    const schedules = loaSchedulesByLoa[loaId] || [];
    const scheduleEnergy = schedules.reduce((total, schedule) => {
      const scheduleStart = parseDateOnlyFromString(schedule.from_date);
      const scheduleEnd = parseDateOnlyFromString(schedule.to_date);
      if (!scheduleStart || !scheduleEnd) return total;

      const overlapDays = overlapDayCount(contractStart, contractEnd, scheduleStart, scheduleEnd);
      if (!overlapDays) return total;

      const timeDifference = timeDifferenceHours(schedule.from_time, schedule.to_time);
      if (!timeDifference) return total;

      const quantum = firstFiniteNumber(schedule.quantum_mw, party.quantum);
      if (quantum === null) return total;

      return total + overlapDays * timeDifference * quantum;
    }, 0);

    if (scheduleEnergy > 0) {
      energyByLoa.set(loaId, scheduleEnergy);
      continue;
    }

    const quantum = firstFiniteNumber(party.quantum);
    energyByLoa.set(loaId, quantum === null ? 0 : daysDifference * 24 * quantum);
  }

  const energyMwh = energyByLoa.size > 0 ? Math.max(...energyByLoa.values()) : 0;
  return { days: daysDifference, energyMwh };
}

function configuredChargesForContract(chargeMasters: Charge[], cutoffDate: string): Charge[] {
  if (!cutoffDate) return [];
  const filtered = (chargeMasters || [])
    .filter((row) => !!row && !!row.active)
    .filter((row) => String(row.effective_date || '').trim() !== '' && String(row.effective_date).trim() <= cutoffDate);

  const byKey = new Map<string, Charge>();
  for (const row of filtered) {
    const key = `${normalizeChargeGroup(row.charge_type)}|${String(row.charge_name || '').trim().toLowerCase()}`;
    const existing = byKey.get(key);
    if (!existing || String(row.effective_date || '') > String(existing.effective_date || '')) {
      byKey.set(key, row);
    }
  }

  return [...byKey.values()].sort((left, right) => {
    const groupCompare = normalizeChargeGroup(left.charge_type).localeCompare(normalizeChargeGroup(right.charge_type));
    if (groupCompare !== 0) return groupCompare;
    const nameCompare = String(left.charge_name || '').localeCompare(String(right.charge_name || ''));
    if (nameCompare !== 0) return nameCompare;
    return String(right.effective_date || '').localeCompare(String(left.effective_date || ''));
  });
}

function resolveChargeMasterFromMasters(
  chargeMasters: Charge[],
  cutoffDate: string,
  chargeLabel: string,
  contractRegionCodes: Set<string>,
): Charge | null {
  const needle = String(chargeLabel || '').trim().toUpperCase();
  if (!needle) return null;

  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const containsNeedle = new RegExp(`\\b${escaped}\\b`, 'i');
  const normalizedNeedle = normalizeChargeAllocationType(chargeLabel);
  let best: Charge | null = null;

  for (const row of chargeMasters || []) {
    if (!row || !row.active) continue;
    const effective = String(row.effective_date || '').trim();
    if (!effective || effective > cutoffDate) continue;

    const group = String(row.charge_type || '').trim();
    const name = String(row.charge_name || '').trim();
    if (!group && !name) continue;

    const normalizedRow = normalizeChargeAllocationType(`${name} ${group}`.trim());
    const matchesGroup = !!group && (group.trim().toUpperCase() === needle || containsNeedle.test(group));
    const matchesName = !!name && (name.trim().toUpperCase() === needle || containsNeedle.test(name));
    const matchesNormalized = !!normalizedNeedle && normalizedRow === normalizedNeedle;
    if (!matchesGroup && !matchesName && !matchesNormalized) continue;

    const requiredRegion = extractRegionCode(name || group);
    if (requiredRegion && !contractRegionCodes.has(requiredRegion)) continue;

    if (!best || String(best.effective_date || '') < effective) {
      best = row;
    }
  }

  return best;
}

function oaChargeAllocationMapFromLoaIds(
  loaIds: string[],
  billConfigsByLoa: Record<string, LoaBillConfig[]>,
): Map<string, OaChargeAllocation> {
  const uniqueLoaIds = (loaIds || []).map((id) => String(id || '').trim()).filter((id) => !!id);
  const map = new Map<string, OaChargeAllocation>();

  for (const loaId of uniqueLoaIds) {
    const items = parseOaChargeAllocation(latestOaChargeAllocation(loaId, billConfigsByLoa));
    for (const item of items) {
      if (!map.has(item.charge_type)) {
        map.set(item.charge_type, item);
      }
    }
  }

  return map;
}

function oaChargeAllocationMap(
  application: ContractChargeApplicationLike,
  billConfigsByLoa: Record<string, LoaBillConfig[]>,
): Map<string, OaChargeAllocation> {
  const loaIds = [String(application.seller_loa_id || '').trim(), String(application.buyer_loa_id || '').trim()].filter((id) => !!id);
  return oaChargeAllocationMapFromLoaIds(loaIds, billConfigsByLoa);
}

function latestOaChargeAllocation(loaId: string, billConfigsByLoa: Record<string, LoaBillConfig[]>): string | null {
  if (!loaId) return null;
  const configs = billConfigsByLoa[loaId] || [];
  const latest = [...configs]
    .filter((item) => (item.bill_type || '').trim().toUpperCase() === 'OA')
    .sort((left, right) => (right.effective_date || '').localeCompare(left.effective_date || ''))[0];
  return latest?.oa_charge_allocation || null;
}

function parseOaChargeAllocation(raw: string | null | undefined): OaChargeAllocation[] {
  if (!raw || !raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => {
        if (!item || typeof item !== 'object') return null;
        const charge_type = normalizeChargeAllocationType(String((item as Partial<OaChargeAllocation>).charge_type || '').trim());
        if (!charge_type) return null;
        const payerInput = String((item as Partial<OaChargeAllocation>).payer || 'Shared');
        const payer: OaChargeAllocation['payer'] = payerInput === 'Buyer' || payerInput === 'Seller' || payerInput === 'Shared' ? payerInput : 'Shared';
        const buyer = Number((item as Partial<OaChargeAllocation>).buyer_percent);
        const seller = Number((item as Partial<OaChargeAllocation>).seller_percent);
        return {
          charge_type,
          payer,
          buyer_percent: Number.isFinite(buyer) ? buyer : 0,
          seller_percent: Number.isFinite(seller) ? seller : 0,
        };
      })
      .filter((item): item is OaChargeAllocation => item !== null);
  } catch {
    return [];
  }
}

function contractRegionCodesFromPartyDetails(partyDetails: ContractChargePartyLike[]): Set<string> {
  const set = new Set<string>();
  for (const party of partyDetails || []) {
    const code = extractRegionCode(party.rldc);
    if (code) set.add(code);
  }
  return set;
}

function extractRegionCode(value: string | null | undefined): string | null {
  const text = String(value || '').trim().toUpperCase();
  if (!text) return null;

  const explicit = /\b(ERLDC|NERLDC|NRLDC|SRLDC|WRLDC)\b/.exec(text);
  if (explicit) return explicit[1];

  if (/\bNORTH\s*EAST\b/.test(text) || /\bNORTHEAST\b/.test(text)) return 'NERLDC';
  if (/\bEAST\b/.test(text) || /\bEASTERN\b/.test(text)) return 'ERLDC';
  if (/\bNORTH\b/.test(text) || /\bNORTHERN\b/.test(text)) return 'NRLDC';
  if (/\bSOUTH\b/.test(text) || /\bSOUTHERN\b/.test(text)) return 'SRLDC';
  if (/\bWEST\b/.test(text) || /\bWESTERN\b/.test(text)) return 'WRLDC';

  return null;
}

function normalizeChargeGroup(value: string | null | undefined): string {
  return String(value || '').trim().toUpperCase().replace(/\s+/g, ' ');
}

function normalizeChargeAllocationType(value: string): string {
  const normalized = value.trim().toUpperCase();
  if (!normalized) return '';
  if (normalized.includes('TRANSMISSION')) return 'Transmission';
  if (normalized.includes('WHEEL')) return 'Wheeling';
  if (normalized.includes('SLDC')) return 'SLDC';
  if (normalized.includes('RLDC')) return 'RLDC';
  if (normalized.includes('LOSS')) return 'Loss';
  if (normalized.includes('APP')) return 'App';
  return value.trim();
}

function resolveConfiguredChargeAmount(charge: Charge, basis: ContractChargeBasis): number {
  const value = Number(charge.value);
  if (!Number.isFinite(value) || value < 0) return 0;
  const computed = resolveChargeAmountFromRate(value, charge.rate_type, basis);
  return Number((Number.isFinite(computed) ? computed : 0).toFixed(2));
}

function resolveChargeAmountFromRate(value: number, rateType: string | null | undefined, basis: ContractChargeBasis): number {
  if (!Number.isFinite(value) || value < 0) return 0;
  const normalized = String(rateType || '').trim();
  if (normalized === 'Rate (₹/MWh)') return value * basis.energyMwh;
  if (normalized === 'Rate (₹/Day)') return value * basis.days;
  return value;
}

function normalizeApplicationChargeType(item: { group_name: string; section_heading?: string | null; sub_group_name: string }, index: number): string {
  const raw = `${item.sub_group_name || ''} ${item.group_name || ''} ${item.section_heading || ''}`.trim();
  if (!raw) return `Charge ${index + 1}`;
  return normalizeChargeAllocationType(raw);
}

function resolveApplicationChargeAmount(
  item: { amount?: number | null; rate_mwh?: number | null; quantum_mwh?: number | null },
): number {
  const amount = Number(item.amount);
  if (Number.isFinite(amount) && amount !== 0) return amount;
  const rate = Number(item.rate_mwh);
  const quantum = Number(item.quantum_mwh);
  return Number.isFinite(rate) && Number.isFinite(quantum) ? rate * quantum : 0;
}

function sanitizePercent(value: number | null | undefined): number {
  if (!Number.isFinite(Number(value))) return 0;
  return Math.min(Math.max(Number(value), 0), 100);
}

function cappedSellerPercent(value: number | null | undefined, buyerPercent: number): number {
  return Math.min(sanitizePercent(value), Math.max(100 - buyerPercent, 0));
}

function parseDateOnlyFromString(value: string | null | undefined): Date | null {
  if (!value) return null;
  const [year, month, day] = value.trim().split('-').map((part) => Number(part));
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function inclusiveDayCount(start: Date, end: Date): number {
  const diffMs = end.getTime() - start.getTime();
  if (diffMs < 0) return 0;
  return Math.floor(diffMs / (24 * 60 * 60 * 1000)) + 1;
}

function overlapDayCount(rangeStart: Date, rangeEnd: Date, scheduleStart: Date, scheduleEnd: Date): number {
  const start = Math.max(rangeStart.getTime(), scheduleStart.getTime());
  const end = Math.min(rangeEnd.getTime(), scheduleEnd.getTime());
  if (end < start) return 0;
  return Math.floor((end - start) / (24 * 60 * 60 * 1000)) + 1;
}

function timeDifferenceHours(fromTime: string | null | undefined, toTime: string | null | undefined): number {
  const fromMinutes = parseTimeToMinutes(fromTime);
  const toMinutes = parseTimeToMinutes(toTime);
  if (fromMinutes === null || toMinutes === null || toMinutes <= fromMinutes) return 0;
  return (toMinutes - fromMinutes) / 60;
}

function parseTimeToMinutes(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = /^(\d{2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || minutes < 0 || minutes >= 60) return null;
  if (hours === 24 && minutes === 0) return 24 * 60;
  if (hours < 0 || hours > 23) return null;
  return hours * 60 + minutes;
}

function firstFiniteNumber(...values: Array<number | string | null | undefined>): number | null {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function emptySummary(): ContractChargeBreakdownSummary {
  return {
    totalAmount: 0,
    buyerAmount: 0,
    sellerAmount: 0,
    seciAmount: 0,
    rows: [],
  };
}

function formatNumber(value: number): string {
  return Number.isFinite(value) ? value.toFixed(2).replace(/\.00$/, '') : '0';
}
